import { computed, ref, shallowRef } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { UserManager } from 'oidc-client-ts'
import type { User } from 'oidc-client-ts'

const AUTHORITY = import.meta.env.VITE_IDP_URL
const CLIENT_ID = import.meta.env.VITE_IDP_CLIENT_ID
const SCOPE = import.meta.env.VITE_IDP_SCOPES
const REDIRECT_URI = `${import.meta.env.VITE_APP_URL}/auth/callback`

export type AuthState = {
  redirectTo?: string
}

export const useAuth = createGlobalState(() => {
  const userManager = shallowRef<UserManager>(getInitializedUserManager())
  const user = ref<User>()

  // used for token validation with idp (only needed if the user is offline for a while)
  const lastSessionValidation = ref(0)
  const SESSION_VALIDATION_INTERVAL = 12 * 60 * 60 * 1000 // 12 hours

  // BroadcastChannel for communicate between tabs
  const broadcastChannel = new BroadcastChannel('abasupport-broadcast-channel')

  async function initializeAuth() {
    registerBroadcastChannelEvents()

    return setUserOrLogin()
  }

  function getInitializedUserManager() {
    const manager = new UserManager({
      authority: AUTHORITY,
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      response_mode: 'fragment',
      post_logout_redirect_uri: window.location.origin,
      scope: SCOPE,
    })

    manager.events.addAccessTokenExpired(() => {
      signInSilent()
    })

    manager.events.addSilentRenewError(() => {
      signOut()
    })

    manager.events.addUserLoaded((_user) => {
      user.value = _user
    })

    manager.events.addUserUnloaded(() => {
      user.value = undefined
    })

    return manager
  }

  function registerBroadcastChannelEvents() {
    broadcastChannel.addEventListener('message', async (event) => {
      if (event.data === 'logout') {
        signOut()
      }
    })
  }

  async function handleLoginCallback() {
    return userManager.value.signinCallback()
  }

  function revokeRefreshTokens() {
    return userManager.value.revokeTokens(['refresh_token'])
  }

  async function setUserOrLogin() {
    const usr = await userManager.value.getUser()

    if (!usr || usr.expired || !usr.access_token) {
      return signIn()
    }

    user.value = usr
  }

  function signIn() {
    return userManager.value.signinRedirect({
      state: {
        redirectTo:
          window.location.pathname +
          window.location.search +
          window.location.hash,
      },
    })
  }

  function signInSilent() {
    return userManager.value.signinSilent()
  }

  async function signOut() {
    await revokeRefreshTokens()
    userManager.value.signoutRedirect({
      post_logout_redirect_uri:
        window.location.origin +
        window.location.pathname +
        window.location.search,
    })
  }

  async function logout() {
    signOut()

    broadcastChannel.postMessage('logout')
  }
  async function getUserFromEndpoint(accessToken: string) {
    const userInfoEndpoint =
      await userManager.value.metadataService.getUserInfoEndpoint()
    const response = await fetch(userInfoEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    if (!response.ok) {
      throw new Error(`Error fetching user: ${response.status}`)
    }
    return response.json()
  }

  async function checkSession() {
    try {
      const currentUser = await userManager.value.getUser()

      if (!currentUser || currentUser.expired || !accessToken.value) {
        user.value = undefined
        await signInSilent()
        return
      }

      // validate token with idp by calling the user info endpoint if we haven't done so recently
      const timeSinceLastValidation = Date.now() - lastSessionValidation.value
      if (
        lastSessionValidation.value === 0 ||
        timeSinceLastValidation > SESSION_VALIDATION_INTERVAL
      ) {
        await getUserFromEndpoint(accessToken.value)
        lastSessionValidation.value = Date.now()
      }
    } catch {
      signIn()
    }
  }

  const authenticated = computed(() => {
    if (!user.value) {
      return false
    }
    return !user.value.expired
  })

  const accessToken = computed(() => {
    return user.value?.access_token
  })

  return {
    userManager,
    authenticated,
    user,
    accessToken,
    initializeAuth,
    handleLoginCallback,
    checkSession,
    logout,
  }
})
